import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, Paper, Skeleton, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import type { LayoutMode } from './components/merge-customization/LayoutToggle';
import { MergeCustomizationHeader } from './components/merge-customization/MergeCustomizationHeader';
import { MergePreviewPanel } from './components/merge-customization/MergePreviewPanel';
import { ModuleSection } from './components/merge-customization/ModuleSection';
import type { PatientModuleData } from './components/merge-customization/PatientColumnLayout';
import { PatientColumnLayout } from './components/merge-customization/PatientColumnLayout';
import { PrimaryPatientSelector } from './components/merge-customization/PrimaryPatientSelector';
import { useDuplicateDetection } from './hooks/useDuplicateDetection';
import { useMergeCustomization } from './hooks/useMergeCustomization';
import type { MergeCustomizationLocationState } from './types/merge-customization';

export const MergeCustomizationPage: React.FC = () => {
  const location = useLocation();
  const state = location.state as MergeCustomizationLocationState | null;

  // No longer requires primaryPatientId — only group + organizationId
  if (!state?.group || !state?.organizationId) {
    return <Navigate to="/patients/merge" replace />;
  }

  return <MergeCustomizationContent state={state} />;
};

/** Inner component so the hook is only called when state is valid. */
function MergeCustomizationContent({ state }: { state: MergeCustomizationLocationState }) {
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('patient');
  const { patientDeduplication } = useEventTracking();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleLayoutChange = useCallback(
    (newMode: LayoutMode) => {
      patientDeduplication.layoutToggled({
        new_layout: newMode,
        previous_layout: layoutMode,
      });
      setLayoutMode(newMode);
    },
    [layoutMode, patientDeduplication],
  );

  const {
    modules,
    selections,
    toggleItem,
    selectAllFromPatient,
    selectAllItemsFromPatient,
    deselectAllItemsFromPatient,
    selectedCount,
    isLoading,
    isMerging,
    fetchErrors,
    confirmMerge,
    patientColors,
    primaryPatientId,
    setPrimaryPatientId,
    activePatients,
    removePatient,
    undoRemovePatient,
    canRemovePatient,
    recentlyRemoved,
  } = useMergeCustomization(state);

  const { isDuplicate } = useDuplicateDetection(modules);

  // --- Undo snackbar for patient removal ---
  const undoSnackbarRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (!recentlyRemoved) return;

    // Dismiss previous undo snackbar if open
    if (undoSnackbarRef.current != null) {
      closeSnackbar(undoSnackbarRef.current);
    }

    const patientName =
      [recentlyRemoved.patient.first_name, recentlyRemoved.patient.last_name]
        .filter(Boolean)
        .join(' ') || recentlyRemoved.patient.patient_id.substring(0, 8);

    undoSnackbarRef.current = enqueueSnackbar(`Removed ${patientName} from merge`, {
      variant: 'default',
      autoHideDuration: 6000,
      action: snackbarId => (
        <Button
          color="inherit"
          size="small"
          onClick={() => {
            undoRemovePatient();
            closeSnackbar(snackbarId);
            patientDeduplication.removalUndone({
              patient_id: recentlyRemoved.patient.patient_id,
              group_id: state.group.group_id,
            });
          }}
          sx={{ fontWeight: 600 }}
        >
          Undo
        </Button>
      ),
    });
  }, [recentlyRemoved]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally only fires on recentlyRemoved change

  // --- Primary change event tracking ---
  const handlePrimaryChange = useCallback(
    (newPrimaryId: string) => {
      if (newPrimaryId === primaryPatientId) return;
      setPrimaryPatientId(newPrimaryId);
      patientDeduplication.primaryChanged({
        new_primary_patient_id: newPrimaryId,
        previous_primary_patient_id: primaryPatientId,
        group_id: state.group.group_id,
      });
    },
    [primaryPatientId, setPrimaryPatientId, patientDeduplication, state.group.group_id],
  );

  const handleRemovePatient = useCallback(
    (patientId: string) => {
      removePatient(patientId);
      patientDeduplication.patientRemovedFromMerge({
        patient_id: patientId,
        group_id: state.group.group_id,
        remaining_patient_count: activePatients.length - 1,
      });
    },
    [removePatient, patientDeduplication, state.group.group_id, activePatients.length],
  );

  const primaryPatient = activePatients.find(p => p.patient_id === primaryPatientId);
  const primaryPatientName = primaryPatient
    ? [primaryPatient.first_name, primaryPatient.last_name].filter(Boolean).join(' ') ||
      primaryPatient.patient_id.substring(0, 8)
    : primaryPatientId.substring(0, 8);

  // Build per-module patient lists (for Layout A)
  const modulePatients = useMemo(() => {
    const result: Record<string, { id: string; name: string; isPrimary: boolean }[]> = {};
    for (const [moduleName, mod] of Object.entries(modules)) {
      const seen = new Set<string>();
      const patients: { id: string; name: string; isPrimary: boolean }[] = [];
      for (const item of mod.items) {
        if (seen.has(item.patientId)) continue;
        seen.add(item.patientId);
        patients.push({
          id: item.patientId,
          name: item.patientName,
          isPrimary: item.isPrimary,
        });
      }
      // Primary patient first
      patients.sort((a, b) => (a.isPrimary === b.isPrimary ? 0 : a.isPrimary ? -1 : 1));
      result[moduleName] = patients;
    }
    return result;
  }, [modules]);

  // Build patient → module → items grouping (for Layout B)
  const patientModuleData = useMemo<PatientModuleData>(() => {
    const result: PatientModuleData = {};
    for (const mod of Object.values(modules)) {
      for (const item of mod.items) {
        result[item.patientId] ??= {};
        (result[item.patientId][item.moduleName] ??= []).push(item);
      }
    }
    return result;
  }, [modules]);

  // Patient list for Layout B — built from active patients
  const allPatients = useMemo(() => {
    return activePatients.map(p => ({
      id: p.patient_id,
      name: [p.first_name, p.last_name].filter(Boolean).join(' ') || p.patient_id.substring(0, 8),
      isPrimary: p.patient_id === primaryPatientId,
    }));
  }, [activePatients, primaryPatientId]);

  const errorPatientIds = Object.keys(fetchErrors);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <MergeCustomizationHeader
        selectedCount={selectedCount}
        isMerging={isMerging}
        onConfirm={confirmMerge}
        layoutMode={layoutMode}
        onLayoutChange={handleLayoutChange}
      />

      {/* Primary Patient Selector */}
      <PrimaryPatientSelector
        patients={activePatients}
        primaryPatientId={primaryPatientId}
        onPrimaryChange={handlePrimaryChange}
        onRemovePatient={handleRemovePatient}
        canRemovePatient={canRemovePatient}
        patientColors={patientColors}
      />

      {/* Info banner */}
      <Alert severity="info" sx={{ mb: 2 }}>
        Review the information from each patient and choose which items to keep.
        Everything you select will carry over into the merged record.
      </Alert>

      {/* Fetch errors */}
      {errorPatientIds.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Some patient data could not be loaded</AlertTitle>
          {errorPatientIds.map(id => (
            <Typography key={id} variant="body2">
              Patient {id.substring(0, 8)}...: {fetchErrors[id]}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Main content */}
      {isLoading ? (
        <Paper sx={{ p: 3 }}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          {/* Left panel */}
          <Box sx={{ flex: 2, minWidth: 0, overflowX: layoutMode === 'patient' ? 'auto' : undefined }}>
            {Object.keys(modules).length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No module data found for the patients in this group.
                </Typography>
              </Paper>
            ) : layoutMode === 'module' ? (
              /* Layout A — By Module (accordion) */
              Object.entries(modules).map(([moduleName, mod], index) => (
                <ModuleSection
                  key={moduleName}
                  module={mod}
                  moduleName={moduleName}
                  selections={selections}
                  onToggleItem={toggleItem}
                  onSelectAllFromPatient={selectAllFromPatient}
                  isDuplicate={isDuplicate}
                  patientColors={patientColors}
                  patients={modulePatients[moduleName] ?? []}
                  defaultExpanded={index === 0}
                />
              ))
            ) : (
              /* Layout B — By Patient (columns) */
              <PatientColumnLayout
                patients={allPatients}
                patientModuleData={patientModuleData}
                selections={selections}
                onToggleItem={toggleItem}
                isDuplicate={isDuplicate}
                patientColors={patientColors}
                onSelectAllFromPatient={selectAllItemsFromPatient}
                onDeselectAllFromPatient={deselectAllItemsFromPatient}
              />
            )}
          </Box>

          {/* Right panel — preview */}
          <Box sx={{ flex: 1, minWidth: 280, display: { xs: 'none', md: 'block' } }}>
            <MergePreviewPanel
              modules={modules}
              selections={selections}
              primaryPatientName={primaryPatientName}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default MergeCustomizationPage;
